import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import './AddressBook.css';
import { API_URL } from '../utils/config';

const BANGLADESH_CITIES = [
  'Dhaka', 'Chittagong', 'Khulna', 'Rajshahi', 'Sylhet',
  'Barishal', 'Rangpur', 'Mymensingh', 'Comilla', 'Narayanganj',
  'Gazipur', 'Jessore', 'Bogura'
];

const AddressBook = () => {
  const { user, isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);
  const [message, setMessage] = useState({ text: '', type: '' });
  
  const [formData, setFormData] = useState({
    label: 'Home',
    fullName: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    isDefault: false
  });

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchAddresses();
  }, [isAuthenticated, navigate]);

  const fetchAddresses = async () => {
    try {
      setLoading(true);
      // FIXED: Changed from single quotes to backticks
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAddresses(response.data.addresses || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching addresses:', error);
      setAddresses([]);
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ text: '', type: '' });

    try {
      if (editingAddress) {
        // Update address
        await axios.put(
          `${API_URL}/api/users/addresses/${editingAddress._id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessage({ text: 'Address updated successfully!', type: 'success' });
      } else {
        // Add new address - FIXED: Changed from single quotes to backticks
        await axios.post(
          `${API_URL}/api/users/addresses`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessage({ text: 'Address added successfully!', type: 'success' });
      }
      
      resetForm();
      fetchAddresses();
    } catch (error) {
      console.error('Error saving address:', error);
      setMessage({ text: 'Error saving address', type: 'error' });
    }
  };

  const handleEdit = (address) => {
    setEditingAddress(address);
    setFormData({
      label: address.label || 'Home',
      fullName: address.fullName || '',
      phone: address.phone || '',
      address: address.address || '',
      city: address.city || '',
      postalCode: address.postalCode || '',
      isDefault: address.isDefault || false
    });
    setShowForm(true);
    window.scrollTo(0, 0);
  };

  const handleDelete = async (addressId) => {
    if (window.confirm('Are you sure you want to delete this address?')) {
      try {
        await axios.delete(
          `${API_URL}/api/users/addresses/${addressId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setMessage({ text: 'Address deleted successfully!', type: 'success' });
        fetchAddresses();
      } catch (error) {
        console.error('Error deleting address:', error);
        setMessage({ text: 'Error deleting address', type: 'error' });
      }
    }
  };

  const resetForm = () => {
    setFormData({
      label: 'Home',
      fullName: '',
      phone: '',
      address: '',
      city: '',
      postalCode: '',
      isDefault: false
    });
    setEditingAddress(null);
    setShowForm(false);
  };

  const getLabelIcon = (label) => {
    const icons = {
      Home: 'fa-home',
      Work: 'fa-briefcase',
      Other: 'fa-map-marker-alt'
    };
    return icons[label] || 'fa-map-marker-alt';
  };

  if (loading) {
    return (
      <div className="loading-page">
        <div className="loading-spinner"></div>
        <p>Loading addresses...</p>
      </div>
    );
  }

  return (
    <div className="address-book-page">
      {/* Page Header */}
      <section className="address-header">
        <div className="address-header-content">
          <h2>Address Book</h2>
          <p>Manage your delivery addresses</p>
        </div>
      </section>

      <div className="address-container section-p1">
        {/* Sidebar Navigation */}
        <div className="address-sidebar">
          <div className="profile-menu">
            <Link to="/profile" className="profile-menu-item">
              <i className="fas fa-user"></i>
              <span>My Profile</span>
            </Link>
            <Link to="/orders" className="profile-menu-item">
              <i className="fas fa-shopping-bag"></i>
              <span>My Orders</span>
            </Link>
            <Link to="/address-book" className="profile-menu-item active">
              <i className="fas fa-map-marker-alt"></i>
              <span>Address Book</span>
            </Link>
          </div>
        </div>

        {/* Main Content */}
        <div className="address-content">
          {/* Message */}
          {message.text && (
            <div className={`address-message ${message.type}`}>
              <i className={`fas fa-${message.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}></i>
              {message.text}
            </div>
          )}

          {/* Add Address Button */}
          {!showForm && (
            <div className="address-actions">
              <button 
                className="btn-add-address"
                onClick={() => setShowForm(true)}
              >
                <i className="fas fa-plus-circle"></i>
                Add New Address
              </button>
            </div>
          )}

          {/* Address Form */}
          {showForm && (
            <div className="address-form-card">
              <div className="address-form-header">
                <h3>
                  <i className="fas fa-map-marked-alt"></i>
                  {editingAddress ? 'Edit Address' : 'Add New Address'}
                </h3>
                <button 
                  className="btn-close-form"
                  onClick={resetForm}
                >
                  <i className="fas fa-times"></i>
                </button>
              </div>

              <form onSubmit={handleSubmit} className="address-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <i className="fas fa-tag"></i>
                      Address Label
                    </label>
                    <select
                      name="label"
                      value={formData.label}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="Home">Home</option>
                      <option value="Work">Work</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>
                      <i className="fas fa-user"></i>
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      placeholder="Enter full name"
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>
                      <i className="fas fa-phone"></i>
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="e.g., 01712345678"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      <i className="fas fa-envelope"></i>
                      Postal Code
                    </label>
                    <input
                      type="text"
                      name="postalCode"
                      value={formData.postalCode}
                      onChange={handleInputChange}
                      placeholder="e.g., 1200"
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>
                    <i className="fas fa-map-marker-alt"></i>
                    Street Address
                  </label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="House/Flat No, Street, Area"
                    rows="3"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>
                    <i className="fas fa-city"></i>
                    City
                  </label>
                  <select
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select City</option>
                    {BANGLADESH_CITIES.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group-checkbox">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="isDefault"
                      checked={formData.isDefault}
                      onChange={handleInputChange}
                    />
                    <span>Set as default address</span>
                  </label>
                </div>

                <div className="form-actions">
                  <button type="submit" className="btn-save-address">
                    <i className="fas fa-save"></i>
                    {editingAddress ? 'Update Address' : 'Save Address'}
                  </button>
                  <button 
                    type="button" 
                    className="btn-cancel-address"
                    onClick={resetForm}
                  >
                    <i className="fas fa-times"></i>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Addresses List */}
          <div className="addresses-grid">
            {addresses.length === 0 ? (
              <div className="no-addresses">
                <div className="no-addresses-icon">
                  <i className="fas fa-map-marked-alt"></i>
                </div>
                <h3>No Addresses Found</h3>
                <p>Add your first delivery address to get started!</p>
                <button 
                  className="btn-add-first"
                  onClick={() => setShowForm(true)}
                >
                  <i className="fas fa-plus"></i>
                  Add Address
                </button>
              </div>
            ) : (
              addresses.map((address) => (
                <div 
                  key={address._id} 
                  className={`address-card ${address.isDefault ? 'default' : ''}`}
                >
                  {address.isDefault && (
                    <span className="default-badge">
                      <i className="fas fa-star"></i>
                      Default
                    </span>
                  )}
                  
                  <div className="address-card-header">
                    <div className="address-label">
                      <i className={`fas ${getLabelIcon(address.label)}`}></i>
                      <h4>{address.label}</h4>
                    </div>
                  </div>

                  <div className="address-details">
                    <p className="address-name">
                      <i className="fas fa-user"></i>
                      {address.fullName}
                    </p>
                    <p className="address-phone">
                      <i className="fas fa-phone"></i>
                      {address.phone}
                    </p>
                    <p className="address-text">
                      <i className="fas fa-map-marker-alt"></i>
                      {address.address}, {address.city} - {address.postalCode}
                    </p>
                  </div>

                  <div className="address-card-actions">
                    <button 
                      className="btn-edit-address"
                      onClick={() => handleEdit(address)}
                    >
                      <i className="fas fa-edit"></i>
                      Edit
                    </button>
                    <button 
                      className="btn-delete-address"
                      onClick={() => handleDelete(address._id)}
                    >
                      <i className="fas fa-trash"></i>
                      Delete
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddressBook;